/**
 * Capture planning: turn a floor's geometry into a grid of things to capture,
 * and score them against where the surveyor has actually walked.
 *
 * Progress is derived entirely from the live position stream. The phone already
 * reports where it is once a second; "what has been captured" is simply "where
 * the operator stood, and for how long". That needs no new data from the SDK.
 */

import {
  boundsOf,
  distanceMeters,
  insideAny,
  metresPerDegreeLng,
  squareRing,
  type LngLat,
} from '@/lib/geo'

export type CaptureModeId = 'off' | 'ble-survey' | 'splat-scan'

export interface CaptureMode {
  id: Exclude<CaptureModeId, 'off'>
  label: string
  hint: string
  /** Grid spacing in metres — how far apart capture targets sit. */
  spacingMeters: number
  /** How close the surveyor must be for a target to count as being worked on. */
  captureRadiusMeters: number
  /**
   * Seconds the surveyor must dwell inside the radius before the target is done.
   * Zero means a single pass is enough.
   */
  requiredDwellSeconds: number
  /** How the targets are drawn: discrete points, or an area coverage grid. */
  render: 'points' | 'cells'
  /** Noun used in the UI: "point" / "cell". */
  unit: string
}

export const CAPTURE_MODES: Record<Exclude<CaptureModeId, 'off'>, CaptureMode> = {
  'ble-survey': {
    id: 'ble-survey',
    label: 'BLE site survey',
    hint: 'Stand on each point until it fills, so the beacon model gets a clean RSSI sample.',
    spacingMeters: 6,
    captureRadiusMeters: 3,
    requiredDwellSeconds: 8,
    render: 'points',
    unit: 'point',
  },
  'splat-scan': {
    id: 'splat-scan',
    label: '3D scan coverage',
    hint: 'Walk every cell once while filming. Amber cells are the gaps left to cover.',
    spacingMeters: 2.5,
    captureRadiusMeters: 3,
    requiredDwellSeconds: 0,
    render: 'cells',
    unit: 'cell',
  },
}

/**
 * Upper bound on grid size.
 *
 * A large venue at 2.5 m spacing can run to tens of thousands of cells, which is
 * both unreadable on screen and pointless to score once a second. Past this the
 * spacing is widened rather than the plan silently truncated — a plan missing
 * its far half would be worse than a coarser one.
 */
const MAX_TARGETS = 4000

export interface CaptureTarget {
  id: string
  lng: number
  lat: number
  /** Seconds of dwell accumulated (or 1 for a pass, in zero-dwell modes). */
  progress: number
}

export interface CapturePlan {
  modeId: Exclude<CaptureModeId, 'off'>
  level: number
  /** Effective spacing, which may be coarser than requested. See `MAX_TARGETS`. */
  spacingMeters: number
  targets: CaptureTarget[]
}

/** The floor outline for a level, falling back to every feature on it. */
function floorFeatures(
  features: GeoJSON.FeatureCollection,
  level: number,
): { outline: GeoJSON.Feature[]; all: GeoJSON.Feature[] } {
  const onLevel = features.features.filter((feature) => {
    const raw = feature.properties?.level
    const featureLevel = typeof raw === 'string' ? Number(raw) : raw
    return typeof featureLevel === 'number' && Math.abs(featureLevel - level) < 1e-9
  })

  const outline = onLevel.filter(
    (feature) =>
      feature.properties?.type === 'floor' &&
      (feature.geometry?.type === 'Polygon' || feature.geometry?.type === 'MultiPolygon'),
  )

  return { outline, all: onLevel }
}

/**
 * Lays a grid over the floor and keeps the cells that fall inside it.
 *
 * Without a floor polygon the whole bounding box is used, which over-covers an
 * L-shaped venue — better than refusing to plan at all, and the operator can see
 * at a glance that targets are sitting outside the walls.
 */
export function buildPlan(
  features: GeoJSON.FeatureCollection,
  level: number,
  mode: CaptureMode,
): CapturePlan | null {
  const { outline, all } = floorFeatures(features, level)
  const bounds = boundsOf(outline.length > 0 ? outline : all)
  if (!bounds) return null

  const heightMeters = (bounds.maxLat - bounds.minLat) * 111_320
  const widthMeters =
    (bounds.maxLng - bounds.minLng) * metresPerDegreeLng((bounds.minLat + bounds.maxLat) / 2)
  if (!(heightMeters > 0) || !(widthMeters > 0)) return null

  let spacing = mode.spacingMeters
  const estimate = (step: number) =>
    Math.ceil(widthMeters / step) * Math.ceil(heightMeters / step)
  while (estimate(spacing) > MAX_TARGETS) spacing *= 1.5

  const dLat = spacing / 111_320
  const targets: CaptureTarget[] = []

  for (let lat = bounds.minLat + dLat / 2; lat <= bounds.maxLat; lat += dLat) {
    const dLng = spacing / metresPerDegreeLng(lat)
    for (let lng = bounds.minLng + dLng / 2; lng <= bounds.maxLng; lng += dLng) {
      if (outline.length > 0 && !insideAny({ lng, lat }, outline)) continue
      targets.push({
        id: `${lng.toFixed(7)}:${lat.toFixed(7)}`,
        lng,
        lat,
        progress: 0,
      })
    }
  }

  return targets.length > 0
    ? { modeId: mode.id, level, spacingMeters: spacing, targets }
    : null
}

export function isDone(target: CaptureTarget, mode: CaptureMode): boolean {
  return mode.requiredDwellSeconds > 0
    ? target.progress >= mode.requiredDwellSeconds
    : target.progress > 0
}

/**
 * Credits the surveyor's current position against the plan.
 *
 * Returns a new target list when something changed, or `null` when nothing did —
 * so a stationary operator does not churn React state (and the map) once a second.
 */
export function creditPosition(
  plan: CapturePlan,
  mode: CaptureMode,
  position: LngLat,
  elapsedSeconds: number,
): CaptureTarget[] | null {
  const radius = mode.captureRadiusMeters
  // Cheap bounding-box reject before the distance maths, so a 4000-cell plan
  // costs a couple of comparisons per cell rather than a hypot.
  const dLat = radius / 111_320
  const dLng = radius / metresPerDegreeLng(position.lat)

  let changed = false
  const next = plan.targets.map((target) => {
    if (isDone(target, mode)) return target
    if (Math.abs(target.lat - position.lat) > dLat) return target
    if (Math.abs(target.lng - position.lng) > dLng) return target
    if (distanceMeters(position, target) > radius) return target

    changed = true
    return {
      ...target,
      progress: mode.requiredDwellSeconds > 0 ? target.progress + elapsedSeconds : 1,
    }
  })

  return changed ? next : null
}

export interface CaptureProgress {
  done: number
  total: number
  /** 0–1. */
  ratio: number
}

export function summarise(plan: CapturePlan, mode: CaptureMode): CaptureProgress {
  const done = plan.targets.reduce((count, target) => count + (isDone(target, mode) ? 1 : 0), 0)
  return { done, total: plan.targets.length, ratio: plan.targets.length ? done / plan.targets.length : 0 }
}

/** The nearest target still to be captured. */
export function nextTarget(
  plan: CapturePlan,
  mode: CaptureMode,
  from: LngLat | null,
): CaptureTarget | null {
  let best: CaptureTarget | null = null
  let bestDistance = Infinity

  for (const target of plan.targets) {
    if (isDone(target, mode)) continue
    if (!from) return target
    const distance = distanceMeters(from, target)
    if (distance < bestDistance) {
      bestDistance = distance
      best = target
    }
  }
  return best
}

/** GeoJSON for the targets: points for survey mode, squares for coverage mode. */
export function planToGeoJSON(
  plan: CapturePlan,
  mode: CaptureMode,
  activeId: string | null,
): { points: GeoJSON.FeatureCollection; cells: GeoJSON.FeatureCollection } {
  const points: GeoJSON.Feature[] = []
  const cells: GeoJSON.Feature[] = []

  for (const target of plan.targets) {
    const done = isDone(target, mode)
    const properties = {
      id: target.id,
      done,
      next: target.id === activeId,
      // 0–1, so a partly dwelled point can render as a filling ring.
      fill:
        mode.requiredDwellSeconds > 0
          ? Math.min(1, target.progress / mode.requiredDwellSeconds)
          : done
            ? 1
            : 0,
    }

    if (mode.render === 'points') {
      points.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [target.lng, target.lat] },
        properties,
      })
    } else {
      cells.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          // Slightly under the spacing, so neighbouring cells read as a grid
          // rather than as one undifferentiated blob.
          coordinates: [squareRing(target, plan.spacingMeters * 0.86)],
        },
        properties,
      })
    }
  }

  return {
    points: { type: 'FeatureCollection', features: points },
    cells: { type: 'FeatureCollection', features: cells },
  }
}
